/**
 * Guest discovery context presentation — icon + one-line example copy per
 * `scenarios.context` slug (#1921, J2 context picker).
 *
 * The seven slugs and their DISPLAY LABELS are the backend's
 * `backend/src/constants/scenarioContexts.js` (frozen mapping of record,
 * #1920/D-007). The icon + example-copy pairing here is presentation-only
 * flavour text for the J2 chip subtitle — not part of that frozen mapping,
 * not persisted anywhere, and safe to revise without a migration. Five of
 * the seven (`at_work`/`leading_people`/`my_own_career`/`personal`/
 * `speaking_up`) carry the exact example copy from the approved Journey
 * Stitch mockup (`docs/design/briefs/mobile-guest-journey-2026-07/
 * claude-design-output/journey-stitch.html`, frame J2). `serving_people`
 * and `school_community` are new as of the 2026-08-06 D-007 amendment (the
 * mockup predates it and only shows six chips) — their copy is authored
 * here, deliberately modest and non-overclaiming, pending any future
 * taxonomy-v2 copy pass the amendment note reserves for later.
 *
 * Pure data — no JSX — so it is trivially unit-testable and importable by
 * both the context shelf and any future consumer (e.g. #1923 learner-home)
 * without dragging in React.
 */
export const CONTEXT_PRESENTATION = Object.freeze({
  at_work: Object.freeze({ icon: '💼', example: 'My manager, my team, a deadline' }),
  serving_people: Object.freeze({ icon: '🤝', example: 'A customer, a patient, someone you serve' }),
  leading_people: Object.freeze({ icon: '👑', example: 'Hard calls, letting someone go' }),
  my_own_career: Object.freeze({ icon: '📈', example: 'Interviews, a raise, saying no' }),
  school_community: Object.freeze({ icon: '🎓', example: 'Classmates, teachers, your community' }),
  personal: Object.freeze({ icon: '👨‍👩‍👧', example: 'Family, friends, money at home' }),
  speaking_up: Object.freeze({ icon: '⚖️', example: 'Ethics, boundaries, whistleblowing' }),
});

const FALLBACK = Object.freeze({ icon: '🗂️', example: '' });

/**
 * @param {string} slug - a `scenarios.context` value
 * @returns {{icon: string, example: string}} never throws on an unknown slug
 *   (falls back to a generic folder icon + empty example) — the same
 *   defensive posture as the backend's `scenarioContextLabel`.
 */
export function contextPresentation(slug) {
  return CONTEXT_PRESENTATION[slug] || FALLBACK;
}
